import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const SoundContext = createContext({
    soundOn: false,
    setSoundOn: () => {},
    musicOn: false,
    setMusicOn: () => {},
    click: () => {},
});

// Tiny click-tick (10 ms square pulse) generated via WebAudio. No external file needed.
function playClickViaWebAudio() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = playClickViaWebAudio._ctx || (playClickViaWebAudio._ctx = new Ctx());
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.type = "square";
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.08);
    } catch (e) {
        /* noop */
    }
}

// Royalty-free **nature soundscape** (Pixabay, public license — forest birds + soft breeze)
const MUSIC_URL =
    "https://cdn.pixabay.com/audio/2024/02/14/audio_aa5e8b4afe.mp3";
// Fallback if the primary URL is unreachable.
const MUSIC_URL_FALLBACK =
    "https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3";

export function SoundProvider({ children }) {
    const [soundOn, setSoundOn] = useState(() => {
        if (typeof window === "undefined") return true;
        return window.localStorage.getItem("g2b_sound") !== "off";
    });
    // Music defaults to ON; browsers will block silent autoplay until the
    // user interacts with the page, so we listen for the first gesture and
    // start playback then.
    const [musicOn, setMusicOn] = useState(() => {
        if (typeof window === "undefined") return true;
        const stored = window.localStorage.getItem("g2b_music");
        if (stored === null) return true; // default ON on first visit
        return stored === "on";
    });
    const audioRef = useRef(null);
    const triedAutoplayRef = useRef(false);

    useEffect(() => {
        try {
            window.localStorage.setItem("g2b_sound", soundOn ? "on" : "off");
        } catch (e) {
            /* noop */
        }
    }, [soundOn]);

    // Create / control the audio element when musicOn changes.
    useEffect(() => {
        try {
            window.localStorage.setItem("g2b_music", musicOn ? "on" : "off");
        } catch (e) {
            /* noop */
        }
        if (musicOn) {
            if (!audioRef.current) {
                const a = new Audio(MUSIC_URL);
                a.loop = true;
                a.volume = 0.22;
                a.preload = "auto";
                a.onerror = () => {
                    // swap to fallback once
                    if (a.src.indexOf(MUSIC_URL_FALLBACK) === -1) {
                        a.src = MUSIC_URL_FALLBACK;
                        a.play().catch(() => {});
                    }
                };
                audioRef.current = a;
            }
            const playPromise = audioRef.current.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(() => {
                    /* autoplay blocked — gesture listener will retry */
                });
            }
        } else if (audioRef.current) {
            audioRef.current.pause();
        }
    }, [musicOn]);

    // Start music on first user gesture (Chrome / Safari autoplay policy).
    useEffect(() => {
        if (!musicOn) return undefined;
        const tryPlay = () => {
            if (triedAutoplayRef.current) return;
            if (!audioRef.current) return;
            const p = audioRef.current.play();
            if (p && typeof p.then === "function") {
                p.then(() => {
                    triedAutoplayRef.current = true;
                    cleanup();
                }).catch(() => {
                    /* try again on next gesture */
                });
            }
        };
        const cleanup = () => {
            document.removeEventListener("click", tryPlay, true);
            document.removeEventListener("keydown", tryPlay, true);
            document.removeEventListener("touchstart", tryPlay, true);
            document.removeEventListener("scroll", tryPlay, true);
        };
        document.addEventListener("click", tryPlay, true);
        document.addEventListener("keydown", tryPlay, true);
        document.addEventListener("touchstart", tryPlay, true);
        document.addEventListener("scroll", tryPlay, { capture: true, passive: true });
        return cleanup;
    }, [musicOn]);

    const click = useCallback(() => {
        if (soundOn) playClickViaWebAudio();
    }, [soundOn]);

    // Global click sfx
    useEffect(() => {
        const handler = (ev) => {
            if (!soundOn) return;
            const el = ev.target?.closest?.("button, a, [role='button']");
            if (el) playClickViaWebAudio();
        };
        document.addEventListener("click", handler, true);
        return () => document.removeEventListener("click", handler, true);
    }, [soundOn]);

    return (
        <SoundContext.Provider value={{ soundOn, setSoundOn, musicOn, setMusicOn, click }}>
            {children}
        </SoundContext.Provider>
    );
}

export function useSound() {
    return useContext(SoundContext);
}
